import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  Injector,
  signal,
  untracked,
  ViewChild,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { finalize } from 'rxjs';
import Fuse from 'fuse.js';
import { I18nPipe } from '../../shared/i18n/i18n-pipe';
import { I18nScopedService } from '../../shared/i18n/i18n-scoped.service';
import { I18nScopeReadyDirective } from '../../shared/i18n/i18n-scope-ready.directive';
import { provideI18nScope } from '../../shared/i18n/i18n-scope.provider';
import { AVAILABLE_LANGUAGES, I18nService } from '../../shared/i18n/i18n.service';
import { HighlightPipe } from '../../shared/pipes/highlight.pipe';
import { PermissionService } from '../../shared/services/permissions/permission.service';
import { ProjectService } from '../../shared/services/project/project.service';
import {
  compareFavoritesFirst,
  FavoriteProjectsService,
} from '../../shared/services/favorite-projects/favorite-projects.service';
import { SessionService } from '../../shared/services/session/session.service';
import { MetricsCommService } from '../../shared/services/metrics/metrics-comm.service';
import { KpiApiService } from '../../features/dashboard/services/kpi-api/kpi-api.service';
import { NotificationDotTestService } from '../../shared/services/notification-dot-test/notification-dot-test.service';
import { DashboardService } from '../../features/dashboard/services/dashboard/dashboard.service';
import { DashboardListItem } from '../../features/dashboard/models/dashboard-list-item/dashboard-list-item.model';
import { MyAuthService } from '../../auth/auth.service';
import { AddDashboardService } from '../dashboard/add-dashboard-dialog/add-dashboard.service';
import { MultiTabImportDialog } from '../multi-tab-import/multi-tab-import-dialog';
import { MultiTabImportService } from '../multi-tab-import/multi-tab-import.service';
import { TabViewService } from '../../shared/tab-view/tab-view.service';
import { DevNotesService } from '../../shared/dev-notes/dev-notes.service';
import { isOpenInNewTabClick } from '../../shared/tab-view/tab-click.util';
import { FifoPreviewSettingsService } from '../../shared/services/fifo-preview/fifo-preview-settings.service';
import { CreateProjectDialogComponent } from '../../features/project-selection/create-project-dialog/create-project-dialog.component';
import { ProjectListItem } from '../../features/project-selection/project-selection';
import {
  COMMAND_PALETTE_NAMESPACES,
  COMMAND_PALETTE_PAGES,
  CommandPalettePage,
} from './command-palette-pages';
import {
  isHiddenForMultiPlant,
  isRequiredForMultiPlant,
} from '../../shared/nav/multi-plant-visibility';
import {
  DEV_COMMANDS,
  DEV_MODE_TRIGGER,
  DevCommand,
  isAnessaEmail,
  recomputeOutcomeMessage,
  recomputeStatusMessage,
} from './command-palette-dev-commands';
import {
  ACCESS_LEVEL,
  AccessLevel,
  RED_RULES,
  RedProposalPreviewDto,
  SECTIONS,
  Section,
  meetsAccessLevel,
} from '@anessa/shared-models';
import { Role } from '../../shared/interfaces/role.interface';
import { RoleService } from '../../shared/services/permissions-management/role.service';
import {
  NotificationService,
  NotificationType,
} from '../../shared/services/notification/notification-service';
import { MonthLockService } from '../../shared/month-lock/month-lock.service';
import { ConfirmationDialog, ConfirmationDialogData } from '../../shared/common/confirmation-dialog/confirmation-dialog';
import { formatAppDate } from '../../shared/date/app-date-format';
import { RedSchedulerClaimService } from '../../shared/red-scheduler-claim/red-scheduler-claim.service';
import { MonthLockStatusDialog } from './month-lock-status-dialog/month-lock-status-dialog';
import { tabTitleFor } from './tab-title';
import type { ProposalReviewDialogData } from '../../features/red/red-proposal-review-dialog/red-proposal-review-dialog';
import { ErrorService } from '../../shared/services/error/error.service';

/** Matches "~changeproject <query>" (any amount of leading/trailing whitespace around the query). */
const CHANGE_PROJECT_ARGS = /^\s*changeproject\s(.*)$/i;
/** Matches "~recompute <query>" the same way. */
const RECOMPUTE_ARGS = /^\s*recompute\s(.*)$/i;
/** Matches "~opendashboard <query>" the same way. */
const OPEN_DASHBOARD_ARGS = /^\s*opendashboard\s(.*)$/i;
/** Matches "~changerole <query>" the same way. */
const CHANGE_ROLE_ARGS = /^\s*changerole\s(.*)$/i;
// "~unlock <YYYY-MM> <reason…>" — the month is the first word, the rest is the reason
// (split in code: a single regex capturing both backtracks super-linearly).
const UNLOCK_ARGS = /^\s*unlock\s(.*)$/i;
const UNLOCK_MONTH = /^\d{4}-\d{2}$/;
const MIN_UNLOCK_REASON_LENGTH = 5;
const UNLOCK_ADD_ARGS = /^\s*unlockadd\s+(\S*)/i;
/** "~redpreview <YYYY-MM>" — the month whose RED re-check to preview. */
const RED_PREVIEW_ARGS = /^\s*redpreview\s+(\S*)/i;
/** "~redhistory <YYYY-MM>" — the month whose RED versions to list. */
const RED_HISTORY_ARGS = /^\s*redhistory\s+(\S*)/i;
/** "~usetabs /a /b …" — the in-app paths to open as tabs, whitespace-separated. */
const USE_TABS_ARGS = /^\s*usetabs\s(.*)$/i;
/** Same in-app-path rule `TabViewService` applies: a leading `/` that isn't the `//host` of a protocol-relative url. */
const IN_APP_PATH = /^\/(?!\/)/;

interface SwitcherPlant {
  id: string;
  name: string;
  isMultiPlant: boolean;
}

interface PlantMetric {
  id: string;
  name: string;
}

interface PaletteRole {
  id: string;
  name: string;
  permissions: readonly string[];
  sectionAccess?: Partial<Record<Section, AccessLevel>>;
  /** Synthetic "exit preview" entry, shown at the top of the list while previewing. */
  reset?: boolean;
}

type PaletteMode =
  | { kind: 'pages' }
  | { kind: 'commands'; query: string }
  | { kind: 'changeproject'; query: string }
  | { kind: 'recompute'; query: string }
  | { kind: 'opendashboard'; query: string }
  | { kind: 'changerole'; query: string }
  | { kind: 'unlock'; month: string; reason: string }
  | { kind: 'unlockadd'; email: string }
  | { kind: 'redpreview'; month: string }
  | { kind: 'redhistory'; month: string }
  | { kind: 'usetabs'; urls: string[] }
  | { kind: 'notes' };

/** Optional MatDialog data — e.g. Layout's Ctrl/Cmd+` shortcut opens straight into dev-command mode. */
export interface CommandPaletteData {
  initialSearchTerm?: string;
}

interface SearchableCommandPalettePage extends CommandPalettePage {
  resolvedLabel: string;
  /** Resolved parent group label (e.g. "Approvisionnements"), shown as a breadcrumb. */
  resolvedGroupLabel?: string;
  /** Resolved `searchKeys` — contributes to matching only, never rendered. */
  resolvedSearchKeys: string[];
  /** `frenchName`, set only for Anessa staff in English: shown and searchable. */
  resolvedFrenchName?: string;
}

@Component({
  selector: 'app-command-palette',
  imports: [
    FormsModule,
    NgTemplateOutlet,
    RouterLink,
    MatIconModule,
    MatProgressSpinnerModule,
    I18nPipe,
    I18nScopeReadyDirective,
    HighlightPipe,
  ],
  providers: [provideI18nScope('ui/command-palette'), I18nScopedService],
  templateUrl: './command-palette.html',
  styleUrl: './command-palette.scss',
})
export class CommandPalette {
  private readonly dialogRef = inject(MatDialogRef<CommandPalette>);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly notificationService = inject(NotificationService);
  private readonly monthLock = inject(MonthLockService);
  private readonly injector = inject(Injector);
  private readonly kpiApi = inject(KpiApiService);
  private readonly permissionService = inject(PermissionService);
  private readonly roleService = inject(RoleService);
  private readonly projectService = inject(ProjectService);
  private readonly addDashboardService = inject(AddDashboardService);
  private readonly favoriteProjects = inject(FavoriteProjectsService);
  private readonly sessionService = inject(SessionService);
  private readonly metricsCommService = inject(MetricsCommService);
  private readonly dashboardService = inject(DashboardService);
  private readonly notificationDotTest = inject(NotificationDotTestService);
  private readonly multiTabImportService = inject(MultiTabImportService);
  private readonly authService = inject(MyAuthService);
  private readonly tabViewService = inject(TabViewService);
  private readonly devNotes = inject(DevNotesService);
  private readonly fifoPreviewSettings = inject(FifoPreviewSettingsService);
  private readonly i18n = inject(I18nService);
  private readonly i18nScoped = inject(I18nScopedService);
  private readonly errorService = inject(ErrorService);
  private readonly redSchedulerClaims = inject(RedSchedulerClaimService);
  private readonly dialogData = inject<CommandPaletteData | null>(
    MAT_DIALOG_DATA,
    { optional: true },
  );

  @ViewChild('searchInput')
  private readonly searchInputRef?: ElementRef<HTMLInputElement>;

  // Pre-filled by Layout's Ctrl/Cmd+` shortcut so the palette opens straight
  // into dev-command mode; the normal Ctrl/Cmd+K path passes no data at all.
  searchTerm = '';
  readonly selectedIndex = signal(0);
  readonly filteredPages = signal<SearchableCommandPalettePage[]>([]);
  readonly mode = signal<PaletteMode>({ kind: 'pages' });
  readonly filteredCommands = signal<DevCommand[]>([]);
  readonly filteredPlants = signal<SwitcherPlant[]>([]);
  protected readonly favoriteProjectIds = this.favoriteProjects.favoriteIds;
  readonly filteredMetrics = signal<PlantMetric[]>([]);
  readonly filteredDashboards = signal<DashboardListItem[]>([]);
  readonly filteredRoles = signal<PaletteRole[]>([]);
  private allRoles: PaletteRole[] | null = null;
  private rolesLoading = false;

  private readonly resolvedPages = signal<SearchableCommandPalettePage[]>([]);
  private allSwitcherPlants: SwitcherPlant[] | null = null;
  private switcherPlantsLoading = false;
  private allPlantMetrics: PlantMetric[] | null = null;
  // Public (unlike the other loading flags) so the template can distinguish
  // "still loading" from "loaded, and there really are no metrics".
  readonly plantMetricsLoading = signal(false);
  /** The month a `~redpreview` is computing, shown on its row until the answer is in. */
  readonly redPreviewLoading = signal<string | null>(null);
  /** The month a `~redhistory` is loading, shown on its row until the list is in. */
  readonly redHistoryLoading = signal<string | null>(null);
  private dashboardsFetchTriggered = false;

  private readonly isMultiPlantProject = computed(
    () => this.projectService.currentProject()?.isMultiPlant ?? false,
  );

  private readonly isDevUser = computed(() =>
    isAnessaEmail(this.authService.ensuredUser()?.email),
  );

  private readonly visiblePages = computed<CommandPalettePage[]>(() =>
    COMMAND_PALETTE_PAGES.filter((page) => {
      const multi = this.isMultiPlantProject();
      const hidden =
        page.hiddenForMultiPlant ||
        (!!page.route && isHiddenForMultiPlant(page.route));
      const required =
        page.requiresMultiPlant ||
        (!!page.route && isRequiredForMultiPlant(page.route));
      if (hidden && multi) return false;
      if (required && !multi) return false;
      // Section-gated pages (routed via `data: { section: SECTIONS.X }`
      // rather than a flat capability) — same VIEW-level check the route
      // guard itself defaults to. See `CommandPalettePage.section`'s doc.
      if (page.section && !this.meetsSections(page)) {
        return false;
      }
      if (!page.permission) return true;

      const permissions = Array.isArray(page.permission)
        ? page.permission
        : [page.permission];
      return permissions.some((permission) =>
        this.permissionService.hasPermission(permission),
      );
    }),
  );

  /** The page's section level on its section or any of its `orSections`. */
  private meetsSections(page: CommandPalettePage): boolean {
    if (!page.section) return true;
    if (!page.sectionLevel && !page.orSections?.length) {
      return this.permissionService.canViewSection(page.section);
    }
    const level = page.sectionLevel ?? ACCESS_LEVEL.VIEW;
    return [page.section, ...(page.orSections ?? [])].some((section) =>
      meetsAccessLevel(this.permissionService.sectionLevel(section), level),
    );
  }

  private readonly visibleCommands = computed<DevCommand[]>(() =>
    DEV_COMMANDS.filter((command) => {
      if (!command.permission) return true;

      const permissions = Array.isArray(command.permission)
        ? command.permission
        : [command.permission];
      return permissions.some((permission) =>
        this.permissionService.hasPermission(permission),
      );
    }),
  );

  // Weighted so a page's own name still wins, but typing its parent group's
  // name (e.g. "reception" for Approvisionnements) still surfaces its
  // sub-pages, since the group itself isn't a navigable entry.
  private readonly fuse = new Fuse<SearchableCommandPalettePage>([], {
    keys: [
      { name: 'resolvedLabel', weight: 0.7 },
      { name: 'resolvedGroupLabel', weight: 0.3 },
      { name: 'resolvedSearchKeys', weight: 0.2 },
      { name: 'resolvedFrenchName', weight: 0.2 },
    ],
    threshold: 0.4,
  });

  constructor() {
    if (this.dialogData?.initialSearchTerm) {
      this.searchTerm = this.dialogData.initialSearchTerm;
    }

    // 'ui/command-palette' loads automatically via I18nScopedService above; page
    // entries reuse labels already registered in their own feature namespaces
    // (e.g. 'ui/sidebar'), so those are loaded explicitly here (same pattern as
    // sidebar.ts loading the foreign 'features/motherdb' scope).
    COMMAND_PALETTE_NAMESPACES.forEach((namespace) =>
      this.i18n.loadScope(namespace),
    );

    effect(() => {
      const foreignScopesReady = COMMAND_PALETTE_NAMESPACES.every((namespace) =>
        this.i18n.getScopeReady(namespace)(),
      );
      if (!foreignScopesReady || !this.i18nScoped.getScopeReady()()) {
        return;
      }

      const showFrenchNames =
        this.isDevUser() && this.i18n.lang() === AVAILABLE_LANGUAGES.EN;
      const resolved = this.visiblePages().map((page) => ({
        ...page,
        resolvedLabel: page.fixedLabel ?? this.i18n.t(page.labelKey, page.namespace),
        resolvedGroupLabel: page.groupLabelKey
          ? this.i18n.t(
              page.groupLabelKey,
              page.groupNamespace ?? page.namespace,
            )
          : undefined,
        resolvedSearchKeys: (page.searchKeys ?? []).map((key) =>
          this.i18n.t(key, page.namespace),
        ),
        resolvedFrenchName: showFrenchNames ? page.frenchName : undefined,
      }));
      this.resolvedPages.set(resolved);
      this.fuse.setCollection(resolved);
      this.applyFilter();

      // The whole template (including #searchInput) is gated behind
      // *appI18nScopeReady, so on a cold session — before any of these
      // namespaces have ever been fetched — the input doesn't exist yet when
      // MatDialog's own autoFocus runs right after open(), and nothing
      // refocuses it once it finally renders. Once scopes are ready (whether
      // that took a real network round trip or was already warm), grab focus
      // ourselves — the same technique the notes panel uses for its textarea
      // (DevNotesPanel). A harmless no-op if the input already has focus (the warm-scope
      // path, where MatDialog's own autoFocus already worked).
      setTimeout(() => this.searchInputRef?.nativeElement.focus());
    });

    // DashboardService.dashboardList is a shared, already-reactive signal (unlike
    // the plant-switcher/metrics lists, which are plain Observables this component
    // fetches itself) — so re-filter reactively once it's loaded, rather than
    // threading a manual callback through fetchDashboardList() like the others.
    // Deliberately re-derives the mode from `parseMode()` instead of reading the
    // `mode` signal — applyFilter() below writes `mode`, so reading it here too
    // would make this effect re-trigger itself every time it runs.
    effect(() => {
      if (
        this.dashboardService.dashboardListLoaded() &&
        this.parseMode().kind === 'opendashboard'
      ) {
        this.applyFilter();
      }
    });

    // "~notes" hands over to the floating notes panel (DevNotesService): the
    // palette closes and the panel opens, focused, where it can be dragged
    // aside and stays usable over other dialogs. The panel used to be a
    // textarea inside the palette itself; it moved out so it could be
    // non-modal (see DevNotesService) and reachable via Ctrl+Alt+N.
    effect(() => {
      if (this.mode().kind === 'notes') {
        untracked(() => {
          this.dialogRef.close();
          this.devNotes.open();
        });
      }
    });
  }

  applyFilter(): void {
    const mode = this.parseMode();
    this.mode.set(mode);
    this.selectedIndex.set(0);

    switch (mode.kind) {
      case 'pages':
        this.filterPages();
        return;
      case 'commands':
        this.filterCommands(mode.query);
        return;
      case 'changeproject':
        this.filterChangeProject(mode.query);
        return;
      case 'recompute':
        this.filterRecompute(mode.query);
        return;
      case 'opendashboard':
        this.filterOpenDashboard(mode.query);
        return;
      case 'changerole':
        this.filterChangeRole(mode.query);
        return;
      case 'unlock':
      case 'unlockadd':
      case 'redpreview':
      case 'redhistory':
      case 'usetabs':
        return; // a single action row, rendered from the parsed arguments.
      case 'notes':
        return; // nothing to filter; the notes panel (DevNotesService) owns its own state.
    }
  }

  private filterPages(): void {
    const term = this.searchTerm.trim();
    this.filteredPages.set(
      term
        ? this.fuse.search(term).map((result) => result.item)
        : this.resolvedPages(),
    );
  }

  private filterCommands(rawQuery: string): void {
    const query = rawQuery.toLowerCase();
    this.filteredCommands.set(
      this.visibleCommands().filter(
        (command) =>
          !query ||
          command.label.toLowerCase().includes(query) ||
          command.id.includes(query),
      ),
    );
  }

  private filterChangeProject(rawQuery: string): void {
    this.ensureSwitcherPlantsLoaded();
    const query = rawQuery.toLowerCase();
    const plants = this.allSwitcherPlants ?? [];
    const matches = query
      ? plants.filter((plant) => plant.name.toLowerCase().includes(query))
      : [...plants];
    matches.sort(compareFavoritesFirst(this.favoriteProjects.favoriteIds()));
    this.filteredPlants.set(matches);
  }

  private filterRecompute(rawQuery: string): void {
    this.ensurePlantMetricsLoaded();
    const query = rawQuery.toLowerCase();
    const metrics = this.allPlantMetrics ?? [];
    this.filteredMetrics.set(
      query
        ? metrics.filter((metric) => metric.name.toLowerCase().includes(query))
        : metrics,
    );
  }

  private filterOpenDashboard(rawQuery: string): void {
    if (
      !this.dashboardService.dashboardListLoaded() &&
      !this.dashboardsFetchTriggered &&
      this.permissionService.canViewSection(SECTIONS.DASHBOARDS)
    ) {
      this.dashboardsFetchTriggered = true;
      this.dashboardService.fetchDashboardList();
    }
    const query = rawQuery.toLowerCase();
    const dashboards = this.dashboardService.dashboardList();
    this.filteredDashboards.set(
      query
        ? dashboards.filter((dashboard) =>
            dashboard.name.toLowerCase().includes(query),
          )
        : dashboards,
    );
  }

  private filterChangeRole(rawQuery: string): void {
    this.ensureRolesLoaded();
    const query = rawQuery.toLowerCase();
    const roles = this.allRoles ?? [];
    const matched = query
      ? roles.filter((role) => role.name.toLowerCase().includes(query))
      : roles;
    // While previewing, offer an "exit preview" entry at the top of the empty
    // query so the role list itself can end the preview.
    const preview = this.permissionService.previewRole();
    const items: PaletteRole[] =
      preview && !query
        ? [
            {
              id: '__reset__',
              name: `Exit preview (as ${preview.name})`,
              permissions: [],
              reset: true,
            },
            ...matched,
          ]
        : matched;
    this.filteredRoles.set(items);
  }

  private ensureRolesLoaded(): void {
    if (this.allRoles || this.rolesLoading) return;
    this.rolesLoading = true;
    this.roleService.getAllRoles().subscribe({
      next: (roles: Role[]) => {
        this.allRoles = roles.map((role) => ({
          id: role._id,
          name: role.name,
          permissions: role.permissions ?? [],
          sectionAccess: role.sectionAccess,
        }));
        this.rolesLoading = false;
        if (this.mode().kind === 'changerole') this.applyFilter();
      },
      error: () => {
        this.allRoles = [];
        this.rolesLoading = false;
      },
    });
  }

  /** Derives the current input mode by parsing `searchTerm` fresh on every keystroke. */
  private parseMode(): PaletteMode {
    if (!this.isDevUser() || !this.searchTerm.startsWith(DEV_MODE_TRIGGER)) {
      return { kind: 'pages' };
    }

    // Any run of leading triggers counts as one: Ctrl/Cmd+` opens the palette
    // already holding "~", so pasting a copied "~usetabs …" line yields "~~usetabs …".
    let rest = this.searchTerm;
    while (rest.startsWith(DEV_MODE_TRIGGER)) rest = rest.slice(DEV_MODE_TRIGGER.length);

    const changeProjectMatch = CHANGE_PROJECT_ARGS.exec(rest);
    if (changeProjectMatch) {
      return { kind: 'changeproject', query: changeProjectMatch[1].trim() };
    }

    const recomputeMatch = RECOMPUTE_ARGS.exec(rest);
    if (recomputeMatch) {
      return { kind: 'recompute', query: recomputeMatch[1].trim() };
    }

    const openDashboardMatch = OPEN_DASHBOARD_ARGS.exec(rest);
    if (openDashboardMatch) {
      return { kind: 'opendashboard', query: openDashboardMatch[1].trim() };
    }

    const changeRoleMatch = CHANGE_ROLE_ARGS.exec(rest);
    if (changeRoleMatch) {
      return { kind: 'changerole', query: changeRoleMatch[1].trim() };
    }

    // Before UNLOCK_ARGS, which would otherwise read "unlockadd" as "unlock".
    const unlockAddMatch = UNLOCK_ADD_ARGS.exec(rest);
    if (unlockAddMatch) {
      return { kind: 'unlockadd', email: unlockAddMatch[1] };
    }

    const redPreviewMatch = RED_PREVIEW_ARGS.exec(rest);
    if (redPreviewMatch) {
      return { kind: 'redpreview', month: redPreviewMatch[1] };
    }

    const redHistoryMatch = RED_HISTORY_ARGS.exec(rest);
    if (redHistoryMatch) {
      return { kind: 'redhistory', month: redHistoryMatch[1] };
    }

    const useTabsMatch = USE_TABS_ARGS.exec(rest);
    if (useTabsMatch) {
      return { kind: 'usetabs', urls: useTabsMatch[1].split(/\s+/).filter(Boolean).map(toAppPath) };
    }

    const unlockMatch = UNLOCK_ARGS.exec(rest);
    if (unlockMatch) {
      const args = unlockMatch[1].trimStart();
      const monthEnd = args.search(/\s/);
      if (monthEnd === -1) return { kind: 'unlock', month: args, reason: '' };
      return { kind: 'unlock', month: args.slice(0, monthEnd), reason: args.slice(monthEnd).trim() };
    }

    const trimmedRest = rest.trim().toLowerCase();
    if (trimmedRest === 'notes') {
      return { kind: 'notes' };
    }

    return { kind: 'commands', query: rest.trim() };
  }

  private ensureSwitcherPlantsLoaded(): void {
    if (this.allSwitcherPlants || this.switcherPlantsLoading) return;
    this.switcherPlantsLoading = true;

    // Favorites go first; re-filter once they arrive in case the projects came first.
    void this.favoriteProjects.ensureLoaded().then(() => {
      if (this.mode().kind === 'changeproject') this.applyFilter();
    });

    this.projectService.getProjectsForSwitcher().subscribe((projects) => {
      this.allSwitcherPlants = projects.map((project) => ({
        id: project.id,
        name: project.projectName ?? project.id,
        isMultiPlant: project.isMultiPlant,
      }));
      this.switcherPlantsLoading = false;
      if (this.mode().kind === 'changeproject') this.applyFilter();
    });
  }

  private ensurePlantMetricsLoaded(): void {
    if (this.allPlantMetrics || this.plantMetricsLoading()) return;

    const plantId = this.projectService.singlePlantId;
    if (!plantId) {
      this.allPlantMetrics = [];
      return;
    }

    this.plantMetricsLoading.set(true);
    this.metricsCommService.getAll(plantId).subscribe((metrics) => {
      this.allPlantMetrics = metrics.map((metric) => ({
        id: metric._id,
        name: metric.displayName || metric.name,
      }));
      this.plantMetricsLoading.set(false);
      if (this.mode().kind === 'recompute') this.applyFilter();
    });
  }

  private currentResultsCount(): number {
    switch (this.mode().kind) {
      case 'pages':
        return this.filteredPages().length;
      case 'commands':
        return this.filteredCommands().length;
      case 'changeproject':
        return this.filteredPlants().length;
      case 'recompute':
        return this.filteredMetrics().length;
      case 'opendashboard':
        return this.filteredDashboards().length;
      case 'changerole':
        return this.filteredRoles().length;
      case 'unlock':
      case 'unlockadd':
      case 'redpreview':
      case 'redhistory':
      case 'usetabs':
        return 1; // the single action row.
      case 'notes':
        return 0; // no list — nothing for arrow keys to move across.
    }
  }

  moveSelection(delta: number): void {
    const count = this.currentResultsCount();
    if (count === 0) return;
    this.selectedIndex.update((index) => (index + delta + count) % count);
  }

  /**
   * Ctrl/Cmd+Enter opens the current result as a new tab instead of navigating
   * in place (see TabViewService) — only meaningful for the two modes whose
   * results are real pages (page results and dashboards); every other mode
   * falls through to its normal selection behavior regardless of the modifier.
   */
  selectCurrent(event?: Event): void {
    const mode = this.mode();
    const keyboardEvent = event as KeyboardEvent | undefined;
    const openInNewTab =
      this.tabViewService.enabled() &&
      !!keyboardEvent &&
      (keyboardEvent.ctrlKey || keyboardEvent.metaKey);

    switch (mode.kind) {
      case 'pages':
        this.selectCurrentPage(openInNewTab);
        return;
      case 'commands':
        this.selectCurrentCommand();
        return;
      case 'changeproject':
        this.selectCurrentPlant();
        return;
      case 'recompute':
        this.selectCurrentMetric();
        return;
      case 'opendashboard':
        this.selectCurrentDashboard(openInNewTab);
        return;
      case 'changerole':
        this.selectCurrentRole();
        return;
      case 'unlock':
        this.confirmUnlock(mode.month, mode.reason);
        return;
      case 'unlockadd':
        this.confirmUnlockAdd(mode.email);
        return;
      case 'redpreview':
        this.runRedPreview(mode.month);
        return;
      case 'redhistory':
        this.runRedHistory(mode.month);
        return;
      case 'usetabs':
        this.useTabs(mode.urls);
        return;
      case 'notes':
        return; // nothing to select — editing happens in the floating notes panel.
    }
  }

  private selectCurrentPage(openInNewTab: boolean): void {
    const page = this.filteredPages()[this.selectedIndex()];
    if (!page) return;
    if (openInNewTab && page.route) {
      this.openAsTab(page.route, page.resolvedLabel);
      return;
    }
    this.select(page);
  }

  private selectCurrentCommand(): void {
    const command = this.filteredCommands()[this.selectedIndex()];
    if (command) this.selectCommand(command);
  }

  private selectCurrentPlant(): void {
    const plant = this.filteredPlants()[this.selectedIndex()];
    if (plant) this.selectPlant(plant);
  }

  private selectCurrentMetric(): void {
    const metric = this.filteredMetrics()[this.selectedIndex()];
    if (metric) this.selectMetric(metric);
  }

  private selectCurrentDashboard(openInNewTab: boolean): void {
    const dashboard = this.filteredDashboards()[this.selectedIndex()];
    if (!dashboard) return;
    if (openInNewTab) {
      this.openAsTab(dashboardRoute(dashboard), dashboard.name);
      return;
    }
    this.selectDashboard(dashboard);
  }

  private selectCurrentRole(): void {
    const role = this.filteredRoles()[this.selectedIndex()];
    if (role) this.selectRole(role);
  }

  /**
   * 'argument' commands seed "~<id> " so the next keystroke enters that
   * command's argument mode; 'info' commands (including 'notes') seed
   * "~<id>" with no argument — 'notes' edits happen in its own textarea,
   * not through the top input. 'immediate' commands just run right away.
   */
  /**
   * Why a dev command has nothing to act on right now, shown greyed out with
   * this note; null when it can run. The unlock commands need an unlock: any
   * to relock, one you opened to add someone.
   */
  commandDisabledReason(command: DevCommand): string | null {
    const unlock = this.monthLock.myUnlock();
    if (command.id === 'relock' && !unlock) return 'No month unlocked';
    if (command.id === 'unlockadd' && !unlock?.isHolder) return 'Open an unlock first';
    return null;
  }

  selectCommand(command: DevCommand): void {
    if (this.commandDisabledReason(command)) return;
    if (command.kind === 'immediate') {
      this.runImmediateCommand(command.id);
      return;
    }

    this.searchTerm =
      command.kind === 'argument'
        ? `${DEV_MODE_TRIGGER}${command.id} `
        : `${DEV_MODE_TRIGGER}${command.id}`;
    this.applyFilter();
  }

  private runImmediateCommand(id: string): void {
    switch (id) {
      case 'newplant':
        this.openCreateProjectDialog();
        return;
      case 'copyid':
        this.copyProjectId();
        return;
      case 'copydashboardid':
        this.copyDashboardId();
        return;
      case 'refreshperms':
        this.dialogRef.close();
        this.permissionService.invalidateAndRefresh();
        return;
      case 'tabs':
        this.dialogRef.close();
        this.tabViewService.toggle();
        return;
      case 'copytabs':
        this.copyTabList();
        return;
      case 'fifopreview':
        this.dialogRef.close();
        this.fifoPreviewSettings.toggle();
        return;
      case 'theme':
        this.dialogRef.close();
        this.sessionService.updateUserTheme();
        return;
      case 'lang':
        this.dialogRef.close();
        this.toggleLanguage();
        return;
      case 'recomputekpis':
        this.recomputeKpis();
        return;
      case 'kpistatus':
        this.dialogRef.close();
        this.kpiApi.recomputeStatus().subscribe((status) => {
          this.notificationService.notify(
            recomputeStatusMessage(status),
            NotificationType.DEFAULT,
            5000,
          );
        });
        return;
      case 'relock':
        this.relockMonth();
        return;
      case 'redclaim':
        this.claimRedScheduling();
        return;
      case 'redrelease':
        this.releaseRedScheduling();
        return;
      case 'lockstatus':
        this.dialogRef.close();
        this.dialog.open(MonthLockStatusDialog, { width: '560px', maxWidth: '95vw' });
        return;
      case 'notifdots': {
        this.dialogRef.close();
        const state = this.notificationDotTest.cycle();
        this.notificationService.notify(
          `Notification dots: ${state}`,
          NotificationType.DEFAULT,
          2000,
        );
        return;
      }
    }
  }

  /**
   * Recompute the active plant's materialized KPIs over the backend's trailing
   * window — the same endpoint and window the nightly cron uses.
   *
   * Fire-and-report rather than fire-and-forget: a sweep takes tens of seconds,
   * so the palette closes immediately with an "in progress" note and a second
   * notification lands when it finishes. Errors go through ErrorService via the
   * api-error interceptor.
   */
  private recomputeKpis(): void {
    const plantId = this.projectService.singlePlantId;
    this.dialogRef.close();
    if (!plantId) {
      this.notificationService.notify(
        'Recompute Custom KPIs: no single plant selected',
        NotificationType.DEFAULT,
        3000,
      );
      return;
    }

    this.notificationService.notify(
      `Recomputing Custom KPIs for ${plantId}…`,
      NotificationType.DEFAULT,
      3000,
    );
    // The sweep runs in the background on the server (the request is answered
    // with 202), so this resolves only once polling says it has finished.
    this.kpiApi.recomputeSweepAndWait(plantId).subscribe((outcome) => {
      this.notificationService.notify(
        recomputeOutcomeMessage(outcome),
        NotificationType.DEFAULT,
        5000,
      );
    });
  }

  selectPlant(plant: SwitcherPlant): void {
    this.dialogRef.close();
    void this.projectService.openProject(plant.id);
  }

  /** Calls the exact same endpoint the per-metric "recompute" button calls. */
  selectMetric(metric: PlantMetric): void {
    this.dialogRef.close();
    const plantId = this.projectService.singlePlantId;
    if (!plantId) return;
    this.metricsCommService.recompute(metric.id, plantId).subscribe();
  }

  /** The `routerLink` for a dashboard result — the same route `selectDashboard` navigates to. */
  dashboardLink(dashboard: DashboardListItem): string {
    return dashboardRoute(dashboard);
  }

  /** Navigates to the same '/dashboard/:id' route the sidebar's own dashboard links use. */
  selectDashboard(dashboard: DashboardListItem): void {
    this.dialogRef.close();
    void this.router.navigateByUrl(dashboardRoute(dashboard));
  }

  /**
   * Previews the platform as the chosen role via PermissionService's read-time
   * lens. Gated on the command's global_admin permission; the backend still
   * authorizes the real user, so this only changes what the UI reveals. The
   * synthetic reset entry exits preview.
   */
  selectRole(role: PaletteRole): void {
    this.dialogRef.close();
    if (role.reset) {
      this.permissionService.clearPreviewRole();
      return;
    }
    this.permissionService.setPreviewRole({
      id: role.id,
      name: role.name,
      permissions: new Set(role.permissions),
      sectionAccess: role.sectionAccess ?? {},
    });
  }

  /** What the single `~unlock` row says for the typed month and reason. */
  unlockPreview(month: string, reason: string): string {
    if (!UNLOCK_MONTH.test(month)) return 'Type ~unlock YYYY-MM followed by the reason';
    if (reason.length < MIN_UNLOCK_REASON_LENGTH) return `Unlock ${month}: now type the reason`;
    return `Unlock ${month} on ${this.currentPlantName()} — "${reason}"`;
  }

  /**
   * `~unlock`: confirms with the plant and month spelled out, then opens the
   * unlock. The server re-checks everything (Anessa account, month locked,
   * one unlock at a time); its refusals surface via the api-error interceptor.
   */
  confirmUnlock(month: string, reason: string): void {
    const plantId = this.projectService.singlePlantId;
    if (!plantId || !UNLOCK_MONTH.test(month) || reason.length < MIN_UNLOCK_REASON_LENGTH) return;
    this.dialogRef.close();
    this.dialog
      .open<ConfirmationDialog, ConfirmationDialogData, boolean>(ConfirmationDialog, {
        data: {
          title: `Unlock ${month} on ${this.currentPlantName()}?`,
          message:
            `Only you will be able to change data dated in ${month} for the next ` +
            `${RED_RULES.unlockMinutes} minutes. Every change is audited with your reason: "${reason}".`,
          confirmLabel: 'Unlock',
        },
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (!confirmed) return;
        this.monthLock.openUnlock(plantId, month, reason).subscribe((unlock) =>
          this.notificationService.notify(
            `${unlock.month} unlocked for you until ${formatAppDate(unlock.expiresAt, 'time')}`,
            NotificationType.DEFAULT,
            4000,
          ),
        );
      });
  }

  /** What the single `~unlockadd` row says for the typed email. */
  unlockAddPreview(email: string): string {
    const unlock = this.monthLock.myUnlock();
    if (!unlock?.isHolder) return 'Open an unlock with ~unlock first';
    if (!email.includes('@')) return 'Type ~unlockadd followed by their email';
    return `Let ${email} change data dated in ${unlock.month} too`;
  }

  /**
   * `~unlockadd`: confirms, naming the person and the month, then adds them to
   * your open unlock. The server checks the limit (RED_RULES.unlockMaxDelegates)
   * and that you opened the unlock; the person's own plant access still applies.
   */
  confirmUnlockAdd(email: string): void {
    const unlock = this.monthLock.myUnlock();
    if (!unlock?.isHolder || !email.includes('@')) return;
    this.dialogRef.close();
    this.dialog
      .open<ConfirmationDialog, ConfirmationDialogData, boolean>(ConfirmationDialog, {
        data: {
          title: `Let ${email} change ${unlock.month}?`,
          message:
            `${email} will be able to change data dated in ${unlock.month} on ${unlock.plantId} ` +
            `until this unlock ends (${formatAppDate(unlock.expiresAt, 'time')}), within their usual ` +
            'access rights. Their changes are audited as part of this unlock.',
          confirmLabel: 'Add',
        },
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (!confirmed) return;
        this.monthLock.addDelegate(email).subscribe(() =>
          this.notificationService.notify(
            `${email} can now change ${unlock.month}`,
            NotificationType.DEFAULT,
            4000,
          ),
        );
      });
  }

  /** What the single `~redpreview` row says for the typed month. */
  redPreviewText(month: string): string {
    if (!UNLOCK_MONTH.test(month)) return 'Type ~redpreview YYYY-MM';
    if (this.redPreviewLoading() === month) return `Checking RED ${month}…`;
    return `Preview what a re-check of RED ${month} on ${this.currentPlantName()} would propose`;
  }

  /**
   * `~redpreview`: what a re-check of the month would propose right now —
   * the review dialog without its decision, or a one-line answer when there
   * is nothing to compare. Read-only; the server checks the Anessa account.
   */
  runRedPreview(month: string): void {
    const plantId = this.projectService.singlePlantId;
    if (!plantId || !UNLOCK_MONTH.test(month) || this.redPreviewLoading()) return;
    // The palette stays open on a spinner while the live figures are computed,
    // and closes once the answer is in.
    this.redPreviewLoading.set(month);
    // Loaded on demand: the palette ships with every page, the RED client and dialog shouldn't.
    void Promise.all([
      import('../../features/red/services/red-api.service'),
      import('../../features/red/red-proposal-review-dialog/red-proposal-review-dialog'),
    ]).then(([{ RedApiService }, { RedProposalReviewDialog }]) =>
      this.injector
        .get(RedApiService)
        .proposalPreview(plantId, month)
        .pipe(finalize(() => this.redPreviewLoading.set(null)))
        .subscribe((preview) => {
          this.dialogRef.close();
          if (preview.outcome !== 'changes') {
            this.notificationService.notify(redPreviewMessage(preview, month), NotificationType.DEFAULT, 5000);
            return;
          }
          if (preview.pending) {
            this.notificationService.notify(
              `A proposal for ${month} is already waiting; this shows it with today's data`,
              NotificationType.DEFAULT,
              5000,
            );
          }
          const data: ProposalReviewDialogData = { ...preview.review, readOnly: true };
          this.dialog.open(RedProposalReviewDialog, { data, width: '960px', maxWidth: '95vw' });
        }),
    ).catch((error: unknown) => {
      // A chunk that failed to load (e.g. after a deploy): free the command
      // again instead of leaving it on its spinner.
      this.redPreviewLoading.set(null);
      this.errorService.handle(error, {
        // Staff-only `~` menu: not translated (see CLAUDE.md).
        userMessage: `~redpreview couldn't load; reload the page and try again`,
        context: { source: 'CommandPalette', action: 'redPreview' },
      });
    });
  }

  /** What the single `~redhistory` row says for the typed month. */
  redHistoryText(month: string): string {
    if (!UNLOCK_MONTH.test(month)) return 'Type ~redhistory YYYY-MM';
    if (this.redHistoryLoading() === month) return `Loading RED ${month} history…`;
    return `Every version of RED ${month} on ${this.currentPlantName()}, rejected ones included`;
  }

  /**
   * `~redhistory`: every version of the month, newest first, in a list that
   * opens each one read-only in the review dialog. The server checks the
   * Anessa account.
   */
  runRedHistory(month: string): void {
    const plantId = this.projectService.singlePlantId;
    if (!plantId || !UNLOCK_MONTH.test(month) || this.redHistoryLoading()) return;
    this.redHistoryLoading.set(month);
    // Loaded on demand, like ~redpreview's client and dialog.
    void Promise.all([
      import('../../features/red/services/red-api.service'),
      import('../../features/red/red-history-dialog/red-history-dialog'),
    ]).then(([{ RedApiService }, { RedHistoryDialog }]) =>
      this.injector
        .get(RedApiService)
        .history(plantId, month)
        .pipe(finalize(() => this.redHistoryLoading.set(null)))
        .subscribe((versions) => {
          this.dialogRef.close();
          const data = { plantId, plantName: this.currentPlantName(), month, versions };
          this.dialog.open(RedHistoryDialog, { data, width: '760px', maxWidth: '95vw' });
        }),
    ).catch((error: unknown) => {
      this.redHistoryLoading.set(null);
      this.errorService.handle(error, {
        // Staff-only `~` menu: not translated (see CLAUDE.md).
        userMessage: `~redhistory couldn't load; reload the page and try again`,
        context: { source: 'CommandPalette', action: 'redHistory' },
      });
    });
  }

  private relockMonth(): void {
    this.dialogRef.close();
    this.monthLock.relock().subscribe((closed) =>
      this.notificationService.notify(
        closed ? `${closed.month} relocked` : 'No month was unlocked',
        NotificationType.DEFAULT,
        3000,
      ),
    );
  }

  /** `~redclaim`: this browser's backend alone runs the RED scheduled jobs for a while. */
  private claimRedScheduling(): void {
    this.dialogRef.close();
    this.redSchedulerClaims.claim().subscribe((claim) =>
      this.notificationService.notify(
        `RED scheduling claimed for this backend until ${formatAppDate(claim.expiresAt, 'time')}`,
        NotificationType.DEFAULT,
        5000,
      ),
    );
  }

  /** `~redrelease`: every backend runs the RED scheduled jobs again. */
  private releaseRedScheduling(): void {
    this.dialogRef.close();
    this.redSchedulerClaims.release().subscribe((released) =>
      this.notificationService.notify(
        released ? 'RED scheduling released: every backend runs it again' : 'RED scheduling was not claimed',
        NotificationType.DEFAULT,
        3000,
      ),
    );
  }

  private currentPlantName(): string {
    const project = this.projectService.currentProject();
    return project?.plantMeta?.name ?? project?.projectName ?? this.projectService.singlePlantId ?? '';
  }

  /** Toggles between the two available languages — same call the topbar's language switcher makes. */
  private toggleLanguage(): void {
    const next =
      this.i18n.lang() === AVAILABLE_LANGUAGES.EN
        ? AVAILABLE_LANGUAGES.FR
        : AVAILABLE_LANGUAGES.EN;
    this.sessionService.updateUserLanguage(next);
  }

  private copyProjectId(): void {
    this.dialogRef.close();
    const id = this.projectService.currentProject()?.id;
    if (id) this.copyToClipboard(id);
  }

  private copyDashboardId(): void {
    this.dialogRef.close();
    const id = this.dashboardService.selectedDashboardID();
    if (id) {
      this.copyToClipboard(id);
      return;
    }
    this.notificationService.notify(
      'No dashboard open',
      NotificationType.ERROR,
      1500,
    );
  }

  /** What the single `~usetabs` row says for the typed/pasted paths. */
  useTabsPreview(urls: string[]): string {
    if (!urls.length) return 'Paste or type paths: ~usetabs /page /other-page';
    const invalid = urls.filter((url) => !IN_APP_PATH.test(url));
    if (invalid.length) return `Not in-app paths: ${invalid.join(', ')}`;
    const max = TabViewService.MAX_TABS;
    const titles = urls.slice(0, max).map((url) => this.tabTitleFor(url));
    const capped = urls.length > max ? ` (only the first ${max})` : '';
    return `Open ${titles.length} tab(s)${capped}: ${titles.join(', ')}`;
  }

  /**
   * `~usetabs`: replaces the open tabs with `urls`, first one active. Refuses
   * the whole list if any entry isn't an in-app path, rather than silently
   * opening a subset of what was pasted.
   */
  useTabs(urls: string[]): void {
    if (!urls.length || urls.some((url) => !IN_APP_PATH.test(url))) return;
    this.dialogRef.close();
    this.tabViewService.replaceTabs(urls.map((url) => ({ url, title: this.tabTitleFor(url) })));
  }

  /** `~copytabs`: the open tabs as a `~usetabs` line, so pasting it back reopens them. */
  private copyTabList(): void {
    this.dialogRef.close();
    const urls = this.tabViewService.tabs().map((tab) => tab.url);
    if (!urls.length) {
      this.notificationService.notify('No tabs open', NotificationType.ERROR, 1500);
      return;
    }
    this.copyToClipboard(`${DEV_MODE_TRIGGER}usetabs ${urls.join(' ')}`);
  }

  private tabTitleFor(url: string): string {
    return tabTitleFor(url, this.i18n, this.dashboardService);
  }

  /**
   * A single-line input drops the line breaks of pasted multi-line text (Chrome
   * joins the lines outright), which would glue a pasted column of paths into
   * one. Line breaks become spaces instead, so `~usetabs` accepts either layout.
   */
  onPaste(event: ClipboardEvent): void {
    const text = event.clipboardData?.getData('text') ?? '';
    const input = event.target as HTMLInputElement | null;
    if (!input || !/[\r\n]/.test(text)) return;
    event.preventDefault();
    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? start;
    const joined = text
      .split(/[\r\n]+/)
      .map((line) => line.trim())
      .filter(Boolean)
      .join(' ');
    input.setRangeText(joined, start, end, 'end');
    this.searchTerm = input.value;
    this.applyFilter();
  }

  private copyToClipboard(value: string): void {
    void navigator.clipboard?.writeText(value);
    this.notificationService.notify(
      `Copied "${value}" to clipboard`,
      NotificationType.DEFAULT,
      1500,
    );
  }

  /**
   * Opens the same CreateProjectDialogComponent the real "New Project" button
   * on the project selection page opens — never a second implementation of
   * project creation. That dialog only reads `.name` off `data.allProjects`
   * (for its unique-name validator), so the placeholder fields below are safe
   * — nothing in the create flow itself reads them.
   */
  private openCreateProjectDialog(): void {
    this.dialogRef.close();

    if (this.allSwitcherPlants) {
      this.openCreateProjectDialogWith(this.allSwitcherPlants);
      return;
    }

    this.projectService.getProjectsForSwitcher().subscribe((projects) => {
      this.allSwitcherPlants = projects.map((project) => ({
        id: project.id,
        name: project.projectName ?? project.id,
        isMultiPlant: project.isMultiPlant,
      }));
      this.openCreateProjectDialogWith(this.allSwitcherPlants);
    });
  }

  private openCreateProjectDialogWith(plants: SwitcherPlant[]): void {
    const allProjects: ProjectListItem[] = plants.map((plant) => ({
      id: plant.id,
      name: plant.name,
      isMultiPlant: plant.isMultiPlant,
      isHealthLoading: false,
      status: null,
      lastData: '',
      lastDataSeconds: null,
      role: '-',
    }));

    this.dialog.open(CreateProjectDialogComponent, {
      width: '480px',
      data: { allProjects },
    });
  }

  select(page: SearchableCommandPalettePage): void {
    this.dialogRef.close();

    if (page.action === 'open-bulk-timeseries-import') {
      this.openBulkTimeseriesImport();
      return;
    }

    if (page.action === 'open-add-dashboard') {
      this.addDashboardService.open();
      return;
    }

    if (page.route) void this.router.navigateByUrl(page.route);
  }

  /**
   * A routed page entry renders as a real `<a [routerLink]>` (see the template) so
   * Ctrl/Cmd/middle-click can open it in a new tab natively; RouterLink itself
   * handles in-place navigation on a plain click, so this only closes the dialog —
   * calling `select()` here would navigate a second time.
   *
   * Only for a plain, unmodified, primary-button click, though — the exact same
   * condition RouterLink's own click handler uses to decide "navigate here" vs.
   * "let the browser open it in a new tab." For a modified click, RouterLink
   * leaves the current view untouched, so the palette must too: closing it here
   * would defeat the point of opening in the background while still browsing.
   */
  onRoutedItemClick(event: MouseEvent, page: SearchableCommandPalettePage): void {
    if (this.openInTabView(event, page.route, page.resolvedLabel)) return;
    if (event.button !== 0 || event.ctrlKey || event.shiftKey || event.altKey || event.metaKey) {
      return;
    }
    this.dialogRef.close();
  }

  /**
   * A dashboard click — same rules as `onRoutedItemClick`: the result is an
   * `<a [routerLink]>`, so a plain click navigates via RouterLink and only
   * closes the dialog here, while a modified/middle click opens a tab and
   * leaves the palette open.
   */
  onDashboardClick(event: MouseEvent, dashboard: DashboardListItem): void {
    if (this.openInTabView(event, dashboardRoute(dashboard), dashboard.name)) return;
    if (event.button !== 0 || event.ctrlKey || event.shiftKey || event.altKey || event.metaKey) {
      return;
    }
    this.dialogRef.close();
  }

  /**
   * With tab view on, a Ctrl/Cmd/middle-click opens `route` as a background
   * in-app tab and keeps the palette open, so several results can be opened in
   * a row — and stops the browser opening its own tab from the
   * `<a routerLink>`. With tab view off it returns false so the browser's
   * native new-tab behavior is kept.
   */
  private openInTabView(event: MouseEvent, route: string | undefined, label: string): boolean {
    if (!route || !this.tabViewService.enabled() || !isOpenInNewTabClick(event)) return false;
    event.preventDefault();
    this.tabViewService.openTab(route, label, { background: true });
    return true;
  }

  /** Closes the palette and opens `route` as an in-app tab (Ctrl+Enter). */
  private openAsTab(route: string, label: string): void {
    this.dialogRef.close();
    this.tabViewService.openTab(route, label);
  }

  private openBulkTimeseriesImport(): void {
    this.dialog
      .open(MultiTabImportDialog, { width: '650px' })
      .afterClosed()
      .subscribe((result) => {
        if (result) this.multiTabImportService.notifyImportCompleted();
      });
  }
}

/** The same '/dashboard/:id' route the sidebar's own dashboard links use. */
function dashboardRoute(dashboard: DashboardListItem): string {
  return `/dashboard/${dashboard.id}`;
}

/** The one-line answer of a `~redpreview` with nothing to compare. */
function redPreviewMessage(preview: Exclude<RedProposalPreviewDto, { outcome: 'changes' }>, month: string): string {
  switch (preview.outcome) {
    case 'not_locked':
      return `${month} isn't a locked, RED-reviewed month on this plant`;
    case 'no_snapshot':
      return `${month} is locked but not frozen yet; opening it on RED freezes it`;
    case 'unchanged':
      return `RED ${month}: no changes since its current version`;
    case 'already_rejected':
      return `RED ${month}: same figures as the correction rejected on ${formatAppDate(preview.rejectedAt, 'datetime')}; nothing new to propose`;
  }
}

/** An address-bar url of this app becomes its in-app path; anything else is left as typed. */
function toAppPath(value: string): string {
  const origin = globalThis.location?.origin;
  return origin && value.startsWith(`${origin}/`) ? value.slice(origin.length) : value;
}
